import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { AnimationEvent } from '@angular/animations';
import { IconComponent, stop, bin, provideIcons, chevronRight } from '../../../shared/icons';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { FOLD_OUT_ANIMATION } from '../../../core/animations/FOLD_OUT_ANIMATION';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors
} from '@angular/forms';
import { PromocodeService } from '../../services/promocode.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagManagerService } from '../../../core/services/tag-manager.service';
import { CurrencyService } from '../../../core/services/currency.service';
import { CurrencyPipe } from '../../../core/pipes/currency.pipe';
import { AmountInterface } from '../../../layout/interfaces/amount-interface';
import { RateInterface } from '../../../layout/interfaces/rate-interface';
import { Observable, first, map, merge, of, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  MentionMeInterface,
  MentionMeService,
  MentionMeSituation,
  MentionMeType
} from '../../../shared/services/mention-me.service';
import { environment } from '../../../../environments/environment';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/public_api';
import { ActioncodeInterface, codetype } from '../../entities/bookingParameters';
import { CartService } from '../../../pages/product-selection/services/cart';
import { LegacyMoneyDto } from '../../../pages/product-selection/generated';
import { CrowdinTranslationService } from '../../services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

@Component({
  selector: 'app-action-code',
  imports: [IconComponent, AlpitranslatePipe, CurrencyPipe, ReactiveFormsModule],
  providers: [provideIcons({ stop, bin, chevronRight })],
  animations: [FOLD_OUT_ANIMATION],
  templateUrl: './action-code.component.html',
  styleUrl: './action-code.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionCodeComponent implements OnInit {
  @Input() codes: string[] = [];
  @Input() totalDeductedFromCoupons: LegacyMoneyDto;
  @Input() implementationPage: string;
  @Input() rates: RateInterface;

  @Output() actionCodeRemoved = new EventEmitter<ActioncodeInterface>(true);
  @Output() actionCodeAdded = new EventEmitter<ActioncodeInterface>(true);

  @ViewChild('couponInput') couponInput!: ElementRef<HTMLInputElement>;

  protected actionCodes: ActioncodeInterface[] = [];
  protected onPromotionCode = false;
  protected searching = false;
  protected display = false;
  protected mentionMeLoaded = false;

  protected form = this.fb.group({
    promotionCode: new FormControl<string>(
      '',
      [],
      [
        (ctrl: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
          // no promocode, no server side check
          if (ctrl.value === '') {
            return of(null);
          }
          return this.promocodeService.currentState.pipe(
            first(null),
            map(state => {
              if (state) {
                return null;
              }
              return { promocodeInvalid: true };
            })
          );
        }
      ]
    )
  });
  protected promocodeState: number;

  protected PROMOCODE_DEFAULT = 0;
  protected PROMOCODE_INVALID = 10;
  protected PROMOCODE_VALID = 20;

  constructor(
    protected currencyService: CurrencyService,
    private fb: FormBuilder,
    private promocodeService: PromocodeService,
    private tagManager: TagManagerService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private mentionMeService: MentionMeService,
    private cartService: CartService,
    private platformInformationService: PlatformInformationService,
    private crowdinTranslationService: CrowdinTranslationService
  ) {}

  loadMentionMe() {
    if (
      !environment.mention_me_refereefind_enabled ||
      this.platformInformationService.getCurrentPartner() ||
      this.platformInformationService.getCurrentPlatformShortcode() !== 'ar'
    ) {
      return;
    }

    this.cartService.cart$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(cart => {
      const orderData: MentionMeInterface = {
        firstname: cart.cart.registerData.firstname,
        surname: cart.cart.registerData.surname,
        email: cart.cart.registerData.email,
        situation: MentionMeSituation.checkout
      };
      this.mentionMeService.loadMentionMeScript(orderData, MentionMeType.refereefind).subscribe(() => {
        this.mentionMeLoaded = true;
      });
    });
  }

  ngOnInit() {
    this.crowdinTranslationService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => this.cdr.markForCheck()
    });
    const isBrowser = this.platformInformationService.getIsBrowser();
    if (isBrowser) {
      delete window['MentionMeFiredTags'];
    }
    merge(this.translateService.onLangChange, this.currencyService.onCurrencyChange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdr.markForCheck();
        if (
          environment.mention_me_refereefind_enabled &&
          !this.platformInformationService.getCurrentPartner() &&
          this.platformInformationService.getCurrentPlatformShortcode() === 'ar'
        ) {
          document.getElementById('mmWrapper').innerHTML = '';
          if (isBrowser) {
            delete window['MentionMeFiredTags'];
          }
          this.loadMentionMe();
        }
      });
    if (this.codes.filter(word => word?.length > 0).length) {
      this.display = true;
      if (!this.mentionMeLoaded) {
        this.loadMentionMe();
      }
      this.searching = true;
      this.searchGivenStringCodes();
    }
    this.promocodeService.onSearching
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((search: boolean) => {
        this.searching = search;
        this.cdr.markForCheck();
      });

    this.promocodeService.newCode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((code: ActioncodeInterface) => {
        // report wrong code, indicate to user
        if (code.errorCode) {
          this.promocodeState = this.PROMOCODE_INVALID;
          this.tagManager.push({
            gtagType: 'event',
            gtagName: 'select_promotion',
            parameters: {
              creative_slot: this.implementationPage,
              promotion_name: code.text,
              promotion_id: 'promocode_invalid'
            }
          });

          // accept code and update cart, cleanup field, report success code
        } else {
          this.promocodeState = this.PROMOCODE_DEFAULT;
          this.form.get('promotionCode').setValue('');
          this.tagManager.push({
            gtagType: 'event',
            gtagName: 'select_promotion',
            parameters: {
              creative_slot: this.implementationPage,
              promotion_name: code.text,
              promotion_id: 'promocode_valid'
            }
          });
          /**
           * BUSINESS LOGIC: there can only be one promo code and one coupon at a time.
           * Newly added promocode/coupon should REPLACE the existing one if there is any.
           *  */
          const idx = this.actionCodes.findIndex(c => c.type === code.type);
          if (idx !== -1) {
            this.actionCodes.splice(idx, 1, code);
          } else {
            this.actionCodes.push(code);
          }

          this.actionCodeAdded.emit(code);
        }
        // inform angular to update view either way
        this.cdr.detectChanges();
      });
  }

  /**
   * during bootup, you can hand over a list of codes to check.
   * It will search for all of them and remove the invalid ones on the fly.
   * To clean up the query params, a 'actionCodeRemoved' event will be triggered
   */
  searchGivenStringCodes() {
    this.codes
      .filter(word => word?.length > 0)
      .forEach(code => {
        this.promocodeService
          .searchCode(code.trim())
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(resp => {
            // if code was confirmed by backend, we add it. Else, we remove it from the cart
            if (!resp.errorCode) {
              this.actionCodeAdded.emit(resp);
              this.actionCodes.push(resp);
            } else {
              this.removeActioncode(resp);
            }
            this.searching = false;
            this.cdr.markForCheck();
          });
      });
  }

  /**
   * the open/close toggle for the action codes
   */
  open() {
    this.display = !this.display;
    if (this.display && !this.mentionMeLoaded) {
      this.loadMentionMe();
    }
  }

  onExpandDone(event: AnimationEvent) {
    if (event.toState === 'void' || !this.display) {
      return;
    }
    this.couponInput.nativeElement.focus();
  }

  /**
   * will give you the coupon if we have one
   * @returns ActioncodeInterface for a coupon
   */
  getCoupon(): ActioncodeInterface {
    return this.actionCodes.find(code => code.type === codetype.coupon);
  }

  /**
   * fire up a search for the entered characters
   */
  clickedRedeem() {
    const phrase = this.form.get('promotionCode').value;
    if (!phrase.length || this.actionCodes.some(c => c.text.toLowerCase() === phrase.toLowerCase())) {
      return;
    }
    this.searchPromocode(phrase);
    this.cdr.markForCheck();
  }

  /** removes an action code from the booking params and the cart if it exists */
  removeActioncode(code: ActioncodeInterface) {
    const index = this.actionCodes.findIndex(c => c.text === code.text);
    if (index > -1) {
      this.actionCodes.splice(
        this.actionCodes.findIndex(c => c.text === code.text),
        1
      );
    }
    this.actionCodeRemoved.emit(code);
  }

  /**
   * used to restore default state of the code field
   * @param phrase
   */
  keyupOnField(phrase: string) {
    if (phrase === '') {
      this.promocodeState = this.PROMOCODE_DEFAULT;
    }
  }

  /**
   * update the promotioncode search to the next input
   * @param phrase input box value
   */
  searchPromocode(phrase: string) {
    const phrase_ = phrase.trim();
    if (this.form.get('promotionCode').value !== '' && phrase_.length) {
      this.searching = true;
      this.promocodeService.updatePhrase(phrase_);
      this.cdr.markForCheck();
    }
  }

  /**
   *
   * @returns the amount that will be left on the coupon code after usage
   */
  totalCouponRemaining(): AmountInterface {
    return {
      amount: this.getCoupon().euroBalanceRaw - this.totalDeductedFromCoupons.amount,
      currency: this.rates
    };
  }
}
